import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useTenantStore } from '@/stores/tenant';
import type { ClientSelection } from '@/types';

const LIMIT = 8;

/** Bumped on every write so all `recents` computeds re-read storage. */
const version = ref(0);

function keyFor(tenantId: string | undefined) {
  return `scribe.client.recents.${tenantId ?? 'default'}`;
}

function read(tenantId: string | undefined): ClientSelection[] {
  try {
    const raw = localStorage.getItem(keyFor(tenantId));
    return raw ? (JSON.parse(raw) as ClientSelection[]) : [];
  } catch {
    return [];
  }
}

function write(tenantId: string | undefined, items: ClientSelection[]) {
  try {
    localStorage.setItem(
      keyFor(tenantId),
      JSON.stringify(items.slice(0, LIMIT)),
    );
  } catch {
    // ignore quota / privacy-mode errors
  }
  version.value++;
}

export function useClientRecents() {
  const { selectedTenantId } = storeToRefs(useTenantStore());
  const recents = computed(() => {
    void version.value;
    return read(selectedTenantId.value);
  });

  function remember(selection: ClientSelection) {
    const next = [
      selection,
      ...read(selectedTenantId.value).filter(
        (item) => item.clientId !== selection.clientId,
      ),
    ];
    write(selectedTenantId.value, next);
  }

  /** Rewrites a stored entry in place, e.g. after the customer was edited. */
  function update(
    clientId: string,
    patch: (item: ClientSelection) => ClientSelection,
  ) {
    const items = read(selectedTenantId.value);
    if (!items.some((item) => item.clientId === clientId)) return;
    write(
      selectedTenantId.value,
      items.map((item) => (item.clientId === clientId ? patch(item) : item)),
    );
  }

  return { recents, remember, update };
}
